import { useEffect, useState } from "react";
import { isNativeApp } from "@/lib/store-billing";
import { storeIntroOfferEligibility } from "@/lib/native-billing";
import type { PremiumState } from "@/lib/premium";

export interface TrialEligibility {
  /** True only when a 3-day free trial may be advertised to this customer. */
  eligible: boolean;
  /** Still asking StoreKit / Play about the introductory offer. */
  loading: boolean;
  /** Where the answer came from — Apple/Google, or our own billing record. */
  source: "store" | "server";
}

/**
 * Decides whether the 3-day introductory free trial may be advertised.
 *
 * Inside the native apps the platform owns introductory-offer eligibility, so
 * StoreKit / Play Billing is the authority; when the wrapper cannot answer we
 * fall back to the server-computed `trial_used` flag. A trial is never granted
 * or re-advertised from local storage or frontend state.
 */
export function useTrialEligibility(state: PremiumState | null | undefined): TrialEligibility {
  const serverEligible = Boolean(state?.authenticated && !state.trial_used && !state.premium_access);
  const native = isNativeApp();
  const [storeEligible, setStoreEligible] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(native);

  useEffect(() => {
    if (!native) {
      setLoading(false);
      return;
    }
    let active = true;
    setLoading(true);
    void storeIntroOfferEligibility("monthly").then((result) => {
      if (!active) return;
      setStoreEligible(result);
      setLoading(false);
    });
    return () => {
      active = false;
    };
  }, [native]);

  if (native && storeEligible !== null) {
    // Apple/Google said no → never advertise or re-grant a trial.
    return { eligible: serverEligible && storeEligible, loading, source: "store" };
  }
  return { eligible: serverEligible, loading, source: "server" };
}